import type { PersistedUserData } from './authMapper';

const STORAGE_KEY = 'hiboo-auth';

export const clearUserFromStorage = (): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;

    const existing = JSON.parse(stored);
    const { user, ...rest } = existing;

    if (Object.keys(rest).length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
    }
  } catch (error) {
    console.error('Failed to clear user from localStorage:', error);
  }
};

/**
 * Validates that user data is not corrupted.
 * Corrupted states:
 * - Has token but missing email or id
 * - Has id/email but no token
 */
const isValidUserData = (data: PersistedUserData | null): boolean => {
  if (!data) return false;

  const hasToken = Boolean(data.token);
  const hasIdOrEmail = Boolean(data.id || data.email);
  const isCorrupted =
    (hasToken && (!data.email || !data.id)) || (!hasToken && hasIdOrEmail);

  return !isCorrupted;
};

/**
 * Loads a valid user from storage.
 * Returns null if no user exists OR if the stored data is corrupted.
 * Corrupted data is automatically cleared from storage.
 */
export const loadValidUserFromStorage = (): PersistedUserData | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);
    const userData = parsed.user || null;

    if (!isValidUserData(userData)) {
      clearUserFromStorage();
      return null;
    }

    return userData;
  } catch (error) {
    console.error('Failed to load user from localStorage:', error);
    return null;
  }
};

export const saveUserToStorage = (userData: PersistedUserData): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const existing = stored ? JSON.parse(stored) : {};

    const updated = {
      ...existing,
      user: userData,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Failed to save user to localStorage:', error);
  }
};
